/**
 * Central endpoint registry — the single home for every pms-api path.
 *
 * Every `*.api.ts` composes URLs as `${pmsBase()}${PMS_ENDPOINTS.X}`,
 * appending dynamic segments at the call site (`.../${id}/something`).
 * No api file writes a raw path literal — a renamed backend route is a
 * one-line change here, and a typo'd constant is a compile error instead
 * of a silent 404.
 *
 * Paths are workspace-relative: `pmsBase()` (lib/base.ts) prefixes the
 * active workspace slug (`/:slug/pms`), so entries here start at the
 * pms router root.
 *
 * Per milestone: M1 (Masters) adds §1–§4; M2 (Form builder) adds §6.
 */
export const PMS_ENDPOINTS = {
  // ── §1. Masters — Competency Categories ────────────────────────────
  CATEGORIES: '/settings/categories',

  // ── §2. Masters — Competencies ─────────────────────────────────────
  COMPETENCIES: '/settings/competencies',
  /** CSV import; appended to COMPETENCIES base, not a standalone path. */
  COMPETENCIES_IMPORT: '/settings/competencies/import',

  // ── §3. Masters — Designation–Competency Map ────────────────────────
  /** Dynamic suffix: `${PMS_ENDPOINTS.MAP}/:designationId` */
  MAP: '/settings/map',

  // ── §4. Masters — Performance Bands ────────────────────────────────
  BANDS: '/settings/bands',
  /** AI audit log — read-only usage/cost ledger for PMS generations. */
  AI_AUDIT: '/settings/ai-audit',
  /**
   * Self-variant generation. DECLARED, not built inline at the call site.
   *
   * The previous version assembled `/forms/:id/self-variant/generate` as a
   * template literal — a path the backend never served, so it answered 503. The
   * contract guard could not catch it: it diffs DECLARED constants, and a path
   * built inline is invisible to it. That blind spot has now produced two 503s
   * (`/forms/:id/questions` was the first), so anything routed belongs here.
   *
   * `:templateId` is substituted by the caller.
   */
  /**
   * Regenerate ONE question, optionally with a wording instruction.
   *
   * Distinct from the bulk endpoint above, which regenerates every Pending
   * question on the template. The wizard's per-question "try again" was calling
   * the bulk path with a single id in the body — which the server ignored, so it
   * would have regenerated the whole form.
   */
  /** POST body: proposed band set; returns overlap/gap errors. */
  BANDS_VALIDATE: '/settings/bands/validate',

  // ── §6. Form Builder — templates ───────────────────────────────────
  /** List/create. Dynamic suffixes: `${FORMS}/:templateId`, `/publish`,
   *  `/duplicate`, `/gate-check`. */
  FORMS: '/forms',

  // ── §7. Run Management ─────────────────────────────────────────────
  /**
   * List/create. Dynamic suffixes: `${RUNS}/:runId`, `/resolve`, `/preview`,
   * `/publish`, `/close`, `/exceptions`, `/extend-expiry`, `/add-participant`,
   * and the participant-scoped `/participants/:participantId/*` actions.
   *
   * There is deliberately NO Runs screen: a run is created and published by
   * step 4 of the form wizard and nowhere else, matching the prototype
   * (`publishStep`, index.html:5217) which builds the run inside the wizard.
   * These endpoints back that one flow plus M4+ read paths.
   */
  FORMS_REUSE: '/forms/:templateId/reuse',
  RUNS: '/runs',
  /** FR-207 reminder + the per-participant remove; suffixes off `${RUNS}/:runId`. */
  RUNS_REMIND: '/runs/:runId/remind',
  RUNS_PARTICIPANT_REMOVE: '/runs/:runId/participants/:participantId/remove',

  // ── §9. Team (RM / observer) evaluations ───────────────────────────
  /**
   * The signed-in user's evaluator queue. Dynamic suffixes:
   * `${TEAM_EVALUATIONS}/:participantId`, `/answers`, `/request-otp`, `/submit`.
   *
   * Scoped to the caller server-side — there is no `?evaluator=` parameter, so
   * one manager cannot read another's reportees.
   */
  TEAM_EVALUATIONS: '/team/evaluations',

  // ── §8. Employee self-evaluation ───────────────────────────────────
  /**
   * The signed-in employee's own evaluations. Dynamic suffixes:
   * `${MY_EVALUATIONS}/:participantId`, `/answers`, `/request-otp`, `/submit`.
   *
   * Scoped to the caller server-side — there is no `?employee=` parameter, so
   * one employee cannot read another's appraisal.
   *
   * FR-410 "dual hat": entirely separate from TEAM_EVALUATIONS. A manager
   * appears in both — once as someone being evaluated, once as an evaluator.
   */
  MY_EVALUATIONS: '/my-evaluations',

  // ── §12. Public token routes ───────────────────────────────────────
  /**
   * The mailed evaluation link. Dynamic suffixes: `${TOKEN}/:token/preview`,
   * `/autosave`, `/request-otp`, `/verify-submit`.
   *
   * The ONLY PMS paths reachable signed OUT — the token is the credential. An
   * account-less employee (directory-only membership, `user_id = null`) can
   * complete their evaluation through these and nothing else.
   */
  TOKEN: '/token',

  /**
   * The mailed MANAGER link (D-27). Dynamic suffixes:
   * `${RM_LINK}/:token/reportees`, then `/reportees/:participantId/preview`,
   * `/autosave`, `/request-otp`, `/verify-submit`.
   *
   * A separate path from `TOKEN` because it is a separate secret resolving to a
   * different role — one link per evaluator per run, opening onto every reportee
   * assigned to them, where `TOKEN` is one link per participant acting only as
   * `Self`.
   */
  RM_LINK: '/rm-link',

  // ── §10. Comparison · 1:1 · Conciliation ───────────────────────────
  /**
   * Dynamic suffixes: `${COMPARISON}/:participantId`, then `/schedule-oto`,
   * `/mark-oto-held`, `/revise`, `/rm-comment`, `/acknowledge`, `/finalize`.
   *
   * Recording read path (pms-api):
   * `${COMPARISON}/:participantId/recording` → GET status/transcript/summary
   * Write paths (start/more-urls/complete/abort) moved to the AI service —
   * they are inlined in recording.api.ts like ai-refine.api.ts, not declared here.
   *
   * FR-502: the READ is identical for both parties. Which write actions are
   * offered comes from `viewer_is_evaluator` on the payload, and is enforced
   * server-side regardless of what the client renders.
   */
  COMPARISON: '/comparison',
  /**
   * Suffix appended to `${COMPARISON}/:participantId` to reach the 1:1 recording
   * resource. Used by recording.api.ts as the base of every recording sub-path.
   */
  COMPARISON_RECORDING: '/recording',

  // ── M8. RBAC — roles, assignments, and the caller's own set ─────────
  /**
   * NOT in the frozen contract (§5 named only email templates). Added under M8's
   * RBAC scope, which is why the guard lists these as extra rather than matched.
   *
   * `ME_PERMISSIONS` is ungated server-side — see rbacApi.getMyAccess.
   */
  ME_PERMISSIONS: '/me/permissions',
  SETTINGS_ROLES: '/settings/roles',
  SETTINGS_USER_ROLES: '/settings/user-roles',
  /** Per-user permission deltas on top of the role template. */
  SETTINGS_USER_OVERRIDES: '/settings/user-roles/:membershipId/overrides',
  /**
   * PMS app-access lifecycle for one member.
   *
   * PUT pauses/resumes, DELETE revokes. Both act on the Workspace-issued grant
   * SCOPED TO PMS — never the workspace membership, never the person.
   */
  SETTINGS_USER_ACCESS: '/settings/user-roles/:membershipId/access',
  /**
   * The EVALUATOR flag for one member — whether evaluation STOPS at them or
   * walks past them up the reporting chain.
   *
   * Deliberately not part of the role assignment: a role says what someone may
   * DO, this says where they sit in the chain, and the backend keeps the two
   * independent (`PmsEvaluatorFlag`, not the role template).
   */
  SETTINGS_USER_EVALUATOR: '/settings/user-roles/:membershipId/evaluator',

  // ── §11. Dashboards & Reports (M7) ─────────────────────────────────
  /**
   * ONE role-dispatched read. The server decides which of the three bodies the
   * caller gets — there is no `?role=` parameter, because one would let any
   * holder of `pms.dashboard.view` read the whole population.
   *
   * D-16 (2026-07-30) struck `/dashboard/run/:runId`, its export, and
   * `/dashboard/results`: the prototype has ONE dashboard, and `06-screens.md`
   * had already combined FR-701 into the Admin body.
   */
  DASHBOARD: '/dashboard',
  DASHBOARD_RESULTS_EXPORT: '/dashboard/results/export',
} as const;

export type PmsEndpoint = (typeof PMS_ENDPOINTS)[keyof typeof PMS_ENDPOINTS];
